// src/reduxStore/selectors/cartSelectors.js
import { createSelector } from "@reduxjs/toolkit";

/**
 * Flatten existingKots -> items array
 */
const flattenExistingKotItems = (existingKots = []) =>
  existingKots.flatMap((kot) => kot.items || []);

/**
 * Merge items from KOTs by variant_uid
 * @param {Array} kots - array of KOT objects [{ items: [...] }, ...]
 */
export const mergeItemsByVariant = (kots = []) => {
  // 1. Flatten
  const allItems = kots.flatMap((kot) => kot.items || []);

  // 2. Merge by variant_uid
  const grouped = {};

  allItems.forEach((item) => {
    const key = item.variant_uid;

    if (!grouped[key]) {
      grouped[key] = {
        ...item,
        quantity: item.quantity || 0,
      };
    } else {
      grouped[key].quantity += item.quantity || 0;
    }
  });

  return Object.values(grouped);
};

/**
 * Get current order details from cart state
 */
export const getCurrentOrderDetails = createSelector(
  (state) => state.cart,
  (cart) => ({
    order_uid: cart.activeOrderId,
    order_id: cart.orderId,
    customer: cart.customerDetails,
    order_type: cart.orderType,
    dine: cart.selectedTable,
    kots: cart.existingKots,
    ...cart.orderInfo,
  })
);

/**
 * selectMergedItems:
 * merges existing sent items + pendingItems and groups by variant_uid
 */
export const selectMergedItems = createSelector(
  [(state) => state.cart.existingKots, (state) => state.cart.pendingItems],
  (existingKots, pendingItems) => {
    const existingFlattened = flattenExistingKotItems(existingKots || []);
    const all = [...existingFlattened, ...(pendingItems || [])];

    const map = {};

    all.forEach((item) => {
      const key = item.variant_uid;
      if (!map[key]) {
        // clone minimal fields we care about; keep current_price too
        map[key] = {
          variant_uid: item.variant_uid,
          variant_name: item.variant_name,
          quantity: item.quantity || 0,
          current_price: item.current_price || {},
          note: item.note || null,
          // keep original items array fields you need (addons, attributes)
        };
      } else {
        map[key].quantity += item.quantity || 0;
      }
    });

    return Object.values(map);
  }
);

/**
 * calculate GST per item (handles gstIncluded or gst_percent fields)
 * expects item.current_price.final_price to be unit price
 */
function calculateGSTForItem(item) {
  const price = item.current_price?.final_price || 0;
  const qty = item.quantity || 0;
  const gstPercent = item.gst_percent ?? item.tax_rule?.gst_percent ?? 0;
  const gstIncluded = item.gst_included ?? false;

  const totalPrice = price * qty;

  if (gstIncluded) {
    const base = totalPrice / (1 + gstPercent / 100);
    const gstAmount = totalPrice - base;
    return {
      baseAmount: Number(base.toFixed(2)),
      gstAmount: Number(gstAmount.toFixed(2)),
      sgst: Number((gstAmount / 2).toFixed(2)),
      cgst: Number((gstAmount / 2).toFixed(2)),
      total: Number(totalPrice.toFixed(2)),
    };
  }

  const gstAmount = (totalPrice * gstPercent) / 100;
  const base = totalPrice;
  return {
    baseAmount: Number(base.toFixed(2)),
    gstAmount: Number(gstAmount.toFixed(2)),
    sgst: Number((gstAmount / 2).toFixed(2)),
    cgst: Number((gstAmount / 2).toFixed(2)),
    total: Number((base + gstAmount).toFixed(2)),
  };
}

/**
 * Bill totals (item base total, gst split, total)
 */
export const selectBillTotals = createSelector([selectMergedItems], (items) => {
  let itemAmount = 0;
  let totalGST = 0;

  items.forEach((item) => {
    const g = calculateGSTForItem(item);
    itemAmount += g.baseAmount;
    totalGST += g.gstAmount;
  });

  return {
    itemAmount: Number(itemAmount.toFixed(2)),
    sgst: Number((totalGST / 2).toFixed(2)),
    cgst: Number((totalGST / 2).toFixed(2)),
    total: Number((itemAmount + totalGST).toFixed(2)),
  };
});

/**
 * discount helper (same as earlier)
 */
function calculateDiscountAmount(amount, discount) {
  if (!discount || !discount.type) return 0;
  if (!discount.value) return 0;

  if (discount.type === "FLAT") return Number(discount.value);
  if (discount.type === "PERCENTAGE")
    return Number(((amount * discount.value) / 100).toFixed(2));
  return 0;
}

/**
 * Final totals (after discount) — use state.cart.discount
 */
export const selectFinalTotals = createSelector(
  [(state) => state.cart.discount, selectBillTotals],
  (discount, totals) => {
    const discountAmount = calculateDiscountAmount(totals.itemAmount, discount);
    const payable = Number((totals.total - discountAmount).toFixed(2));
    return {
      ...totals,
      discountAmount,
      payable,
    };
  }
);

/**
 * Addons summary helper
 */
export const getAddonSummary = (addons = []) => {
  if (!addons.length) return null;

  const count = addons.reduce((sum, a) => sum + (a.qty || 1), 0);
  const total = addons.reduce((sum, a) => sum + a.price * (a.qty || 1), 0);

  return { count, total };
};

/**
 * Variant attribute label helper
 * e.g. [{ attribute_name: "Size", attribute_value: "Large" }, { attribute_name: "Spice", attribute_value: "Medium" }]
 * returns "(Large • Medium)"
 */

export const getVariantAttributeLabel = (attributes = []) => {
  if (!Array.isArray(attributes)) return "";

  const values = attributes.map((a) => a?.attribute_value).filter(Boolean); // removes null, undefined, ""

  if (values.length === 0) return "";

  return `(${values.join(" • ")})`;
};